import { useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import type { EntWebsiteBlockAssembly } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type {
  EntWebsiteBlockAssemblyIdTypeOnlyFragment,
  EntWebsiteNativeDataSourceFragment,
  PageAssemblyQueryQuery,
  PageAssemblyQueryQueryVariables,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { EntWebsiteContext } from 'bundles/ent-website/components/EntWebsiteContext';
import { pageAssemblyQuery } from 'bundles/ent-website/queries';
import type { NonNullableArray } from 'bundles/ent-website/types';

const NOT_FOUND_PAGE_SLUG = '404';

type PropsFromGraphQL = {
  title: string;
  seoDescription?: string;
  seoTitle?: string;
  seoImageUrl?: string;
  seoCanonical?: string;
  dataSources: Array<EntWebsiteNativeDataSourceFragment>;
  preventSearchEngine: boolean;
  blocks: Array<
    EntWebsiteBlockAssemblyIdTypeOnlyFragment & Pick<EntWebsiteBlockAssembly, 'experimentName' | 'experimentValue'>
  >;
};

export default function usePageAssemblyQuery(slug: string, domain: string): PropsFromGraphQL {
  const [overrideSlug, setOverrideSlug] = useState<string | null>(null);
  const { isPreview, locale } = useContext(EntWebsiteContext);
  const { data, loading } = useQuery<PageAssemblyQueryQuery, PageAssemblyQueryQueryVariables>(pageAssemblyQuery, {
    context: { clientName: getClientName('ENTERPRISE', isPreview) },
    variables: {
      slug: overrideSlug ?? slug,
      preview: isPreview,
      // If slug is not found querieng 404 page. This is safer if someone decides to adds new domain and forgets to update Contentful.
      domain: overrideSlug === NOT_FOUND_PAGE_SLUG ? 'dev' : domain,
      locale,
    },
    errorPolicy: 'all',
  });
  const page = data?.entWebsitePageAssemblyCollection?.items?.[0];
  useEffect(() => {
    if (!loading && !page?.slug) {
      setOverrideSlug(NOT_FOUND_PAGE_SLUG);
    }
  }, [loading, page?.slug]);

  const dataSources = page?.dataSourcesCollection?.items.filter((m) => !!m) ?? [];

  const blocks = page?.blocksCollection?.items.filter((m) => !!m) ?? [];

  return {
    title: page?.title ?? '',
    seoTitle: page?.seoTitle ?? '',
    seoDescription: page?.seoDescription ?? undefined,
    seoImageUrl: page?.seoImage?.url ?? undefined,
    seoCanonical: page?.seoCanonical ?? undefined,
    preventSearchEngine: page?.preventSearchEngine ?? false,
    dataSources: dataSources as NonNullableArray<typeof dataSources>,
    blocks: blocks as NonNullableArray<typeof blocks>,
  };
}
